import React, { useEffect, useRef, useState } from 'react'

import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'

import type { AppSection } from '../../../types'
import { PreviewSetting } from './PreviewSetting'
import * as S from './PreviewTab.styled'

type PreviewTabProps = {
  settings: AppSection['settings']
  iframeUrl: string
  hmac: string
}

type MembershipType = 'none' | 'free' | 'paid'

async function calculateHmac(
  hmacKey: string,
  shopId: string,
  supporterId: string,
  tierId: string,
  timestamp: string,
) {
  if (!crypto?.subtle) {
    throw new Error('Crypto is not supported in this browser')
  }

  const message = `shop_id=${shopId}&supporter_id=${supporterId}&tier_id=${tierId}&timestamp=${timestamp}`

  // Import the HMAC key
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(hmacKey),
    { name: 'HMAC', hash: 'SHA-512' },
    false,
    ['sign'],
  )

  // Sign the message
  const signature = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(message))

  // Convert to hex string
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export const PreviewTab = ({ settings, iframeUrl, hmac }: PreviewTabProps) => {
  const iframeContainerRef = useRef<HTMLDivElement>(null)
  const { currentShop } = useCurrentShop()
  const [queryParams, setQueryParams] = useState<string>('')
  const [queryParamValues, setQueryParamValues] = useState<Record<string, string>>({})
  const [membershipType, setMembershipType] = useState<MembershipType>('none')

  useEffect(() => {
    const calculateQp = async () => {
      if (!currentShop) return

      const timestamp = (Date.now() / 1000).toString()

      // Determine supporter and tier IDs based on membership type
      let supporterId = ''
      let tierId = ''

      switch (membershipType) {
        case 'none':
          supporterId = 'none'
          tierId = 'none'
          break
        case 'free':
          supporterId = 'free-supporter-123'
          tierId = 'none'
          break
        case 'paid':
          supporterId = 'paid-supporter-789'
          tierId = 'premium-tier-101'
          break
      }

      const hmacSignature = await calculateHmac(
        hmac,
        currentShop.id,
        supporterId,
        tierId,
        timestamp,
      )

      const baseParams: Record<string, string> = {
        hmac: hmacSignature,
        shop_id: currentShop.id,
        timestamp,
        ...queryParamValues,
      }

      // Only add supporter and tier IDs if they exist
      if (supporterId) {
        baseParams.supporter_id = supporterId
      }
      if (tierId) {
        baseParams.tier_id = tierId
      }

      const qp = new URLSearchParams(baseParams).toString()
      setQueryParams(qp)
    }
    calculateQp()
  }, [hmac, currentShop?.id, queryParamValues, membershipType])

  return (
    <S.PreviewContainer>
      <S.SettingsLabel>Settings</S.SettingsLabel>
      <S.PreviewWrapper>
        <S.Preview>
          {settings.map((setting) => {
            return (
              <PreviewSetting
                key={setting.id}
                setting={{
                  ...(setting as any),
                  value: '',
                }}
                onSettingUpdate={(k, v) => {
                  setQueryParamValues({
                    ...queryParamValues,
                    [k]: v?.toString() || '',
                  })
                }}
              />
            )
          })}
        </S.Preview>
        <S.IframeContainer ref={iframeContainerRef}>
          <S.ScaledIframe src={`${iframeUrl.trim()}?${queryParams}`} scaleRatio={0.5} />
        </S.IframeContainer>
      </S.PreviewWrapper>
      <S.MembershipToggle>
        <S.MembershipLabel>Membership Type:</S.MembershipLabel>
        <S.MembershipButtons>
          <S.MembershipButton
            $active={membershipType === 'none'}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setMembershipType('none')
            }}
          >
            None
          </S.MembershipButton>
          <S.MembershipButton
            $active={membershipType === 'free'}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setMembershipType('free')
            }}
          >
            Free
          </S.MembershipButton>
          <S.MembershipButton
            $active={membershipType === 'paid'}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setMembershipType('paid')
            }}
          >
            Paid
          </S.MembershipButton>
        </S.MembershipButtons>
      </S.MembershipToggle>
    </S.PreviewContainer>
  )
}
